package com.macrofocus.eflux.components

import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.selection.Selection
import com.macrofocus.eflux.network.CombinedBranches
import com.macrofocus.eflux.network.CombinedBuses
import com.macrofocus.eflux.network.Link
import com.macrofocus.eflux.network.Node
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.network.crossborder.crossborder
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.risk.RiskComponent
import com.macrofocus.eflux.network.schema.Schema
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.toRawString
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.data.LineButton
import i18next.useTranslation
import js.objects.unsafeJso
import mui.icons.material.CloseTwoTone
import mui.icons.material.ZoomOutMapTwoTone
import mui.material.*
import mui.material.Size
import mui.material.styles.Theme
import mui.material.styles.TypographyVariant
import mui.material.styles.useTheme
import mui.system.Box
import mui.system.responsive
import mui.system.sx
import org.mkui.react.useProperty
import org.mkui.react.useSingleSelection
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.span
import react.useRequired
import web.cssom.*

external interface InfoProps : Props {
    var primary: Boolean
    var probing: MutableSingleSelection<Any>?
    var selection: MutableSingleSelection<Any>?
    var ignoreProbing: Selection<Any>?
    var hideCrossborder: MutableSingleSelection<Any>?
}

val Info = FC<InfoProps> { props ->
    val theme = useTheme<Theme>()
    val context = useRequired(EFluxContext)
    val (t, _) = useTranslation()

    val dataLoader = context.dataLoader

    val (expertMode, _) = useProperty(dataLoader.exportMode)

    val (getPhysicalNetwork, _) = useProperty(dataLoader.physicalNetwork)
    val (getLogicalNetwork, _) = useProperty(dataLoader.logicalNetwork)
    val (getSubstationNetwork, _) = useProperty(dataLoader.substationNetwork)

    val (getProbing, _) = if(props.probing != null) useSingleSelection(props.probing!!) else Pair(null, null)
    val (getSelection, _) = if(props.selection != null) useSingleSelection(props.selection!!) else Pair(null, null)
    val (getHideCrossBorder, _) = if(props.hideCrossborder != null) useSingleSelection(props.hideCrossborder!!) else Pair(null, null)

    val current = if(props.ignoreProbing == null || !props.ignoreProbing!!.isSelected(getProbing))
        dataLoader.controller.top(getProbing, getSelection)
    else {
        null
    }
    val extra = dataLoader.controller.extra(getProbing, getSelection)

    val (riskTab, setRiskTab) = useProperty(context.riskTab)
    val (coordinateTab, setCoordinateTab) = useProperty(context.coordinateTab)

    val point = if (current != null && current is LogicalBusId) {
        val substation: Substation? = getSubstationNetwork?.substations?.get(current)
        if (substation != null) {
            object : Node {
                override val id: LogicalBusId
                    get() = substation.logicalBusId
                override val bus: CombinedBuses?
                    get() = substation.buses
                override val fullName: String
                    get() = substation.name ?: substation.logicalBusId.name
            }
        } else {
            null
        }
    } else {
        null
    }

    val extraPoint = if (extra != null && extra is LogicalBusId) {
        val substation: Substation? = getSubstationNetwork?.substations?.get(extra)
        if (substation != null) {
            object : Node {
                override val id: LogicalBusId
                    get() = substation.logicalBusId
                override val bus: CombinedBuses?
                    get() = substation.buses
                override val fullName: String
                    get() = substation.name ?: substation.logicalBusId.name
            }
        } else {
            null
        }
    } else {
        null
    }

    val path = if (current != null && current is LogicalBranchId) {
        val line: Line? = getSubstationNetwork?.lines?.get(current)
        if (line != null) {
            object : Link {
                override val id: LogicalBranchId
                    get() = line.logicalBranchId
                override val branch: CombinedBranches?
                    get() = line.branches
                override val longName: String
                    get() = line.longName ?: line.logicalBranchId.name
            }
        } else {
            null
        }
    } else {
        null
    }

    val extraPath = if (extra != null && extra is LogicalBranchId) {
        val line: Line? = getSubstationNetwork?.lines?.get(extra)
        if (line != null) {
            object : Link {
                override val id: LogicalBranchId
                    get() = line.logicalBranchId
                override val branch: CombinedBranches?
                    get() = line.branches
                override val longName: String
                    get() = line.longName ?: line.logicalBranchId.name
            }
        } else {
            null
        }
    } else {
        null
    }

    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    val (masterDateTime, _) = useProperty(context.configuration.masterDateTime)

    // Display nothing if no selection is made
    if(props.probing == null && getSelection == null) {
        return@FC
    }

    // Display nothing if no probing and no selection are made
    if(props.probing != null && getProbing == null && props.selection == null && props.hideCrossborder != null && getHideCrossBorder != null) {
        return@FC
    }

    // Display nothing if this displays the probing and the selection is the same as the probing
    if(props.probing != null && getProbing != null && getProbing == getHideCrossBorder) {
        return@FC
    }

    Box {
        sx {
            display = Display.inlineFlex
            marginLeft = 10.px
            marginRight = 10.px
            if(extra != null) {
                minHeight = 200.px
            } else {
                minHeight = 0.px
            }
            if (props.selection != null && extra == null) {
                height = 200.px
            } else if(props.probing != null) {
                height = 200.px
            }
        }

        Stack {
            direction = responsive(StackDirection.column)
//            asDynamic().justifyContent = JustifyContent.center

            if (current is RiskComponent) {
                sx {
                    marginTop = 10.px
                }

                RiskComponentInfo {
                    this.riskComponent = current
                }
            } else if (current is Schema) {
                if (dataLoader.schemaSelection.isSelected(current)) {
                    Fab {
                        val color = theme.asDynamic().palette.text.primary
                        val backgroundColor = theme.asDynamic().palette.background.default
                        sx = unsafeJso {
                            position = Position.absolute
                            top = 1.px
                            right = 1.px
                            width = 24.px
                            height = 24.px
                            minWidth = 0.px
                            minHeight = 0.px
                            this.color = color
                            this.backgroundColor = backgroundColor
                        }

                        //            classes(classes.root)
                        size = Size.small
                        title = "Close"
                        ariaLabel = "Close"
                        //            color = ButtonColor.inherit
                        onClick = {
                            dataLoader.schemaSelection.clearSelection()
                        }

                        CloseTwoTone {
                        }
                    }
                }

                SchemaInfo {
                    this.schema = current
                }
            } else if (path != null) {
                val line: Line = getSubstationNetwork?.lines?.get(current)!!

                sx {
//                    marginTop = 10.px
                    marginBottom = 10.px

                    if(extra == null) {
                        overflowY = Overflow.hidden
                    }
                }

                if (dataLoader.logicalBranchSelection.isSelected(line.logicalBranchId)) {
                    Fab {
                        val color = theme.asDynamic().palette.text.primary
                        val backgroundColor = theme.asDynamic().palette.background.default
                        sx = unsafeJso {
                            position = Position.absolute
                            top = 1.px
                            right = 1.px
                            width = 24.px
                            height = 24.px
                            minWidth = 0.px
                            minHeight = 0.px
                            this.color = color
                            this.backgroundColor = backgroundColor
                        }

                        //            classes(classes.root)
                        size = Size.small
                        title = "Close"
                        ariaLabel = "Close"
                        //            color = ButtonColor.inherit
                        onClick = {
                            dataLoader.logicalBranchSelection.clearSelection()
                        }

                        CloseTwoTone {
                        }
                    }
                }

                LinkInfo {
                    this.primary = props.primary
                    link = path
                    tooltipLinks = context.configuration.tooltipLinks
                    this.masterDateTime = masterDateTime.toRawString()
                }
            } else if (point != null) {
                val substation: Substation = getSubstationNetwork?.substations?.get(current)!!

                sx {
//                    marginTop = 10.px
                    marginBottom = 10.px
                }

                val selection =
                    if (props.primary) dataLoader.controller.secondaryLogicalBusSelection else dataLoader.controller.primaryLogicalBusSelection
                if (selection.isSelected(substation.logicalBusId)) {
                    Fab {
                        val color = theme.asDynamic().palette.text.primary
                        val backgroundColor = theme.asDynamic().palette.background.default
                        sx = unsafeJso {
                            position = Position.absolute
                            top = 1.px
                            right = 1.px
                            width = 24.px
                            height = 24.px
                            minWidth = 0.px
                            minHeight = 0.px
                            this.color = color
                            this.backgroundColor = backgroundColor
                        }

                        //            classes(classes.root)
                        size = Size.small
                        title = "Close"
                        ariaLabel = "Close"
                        //            color = ButtonColor.inherit
                        onClick = {
                            dataLoader.logicalBusSelection.clearSelection()
                        }

                        CloseTwoTone {
                        }
                    }
                }

                NodeInfo {
                    this.primary = props.primary
                    node = point
                    tooltipNodes = context.configuration.tooltipNodes
                    this.masterDateTime = masterDateTime.toRawString()
                }
            } else if (current is Coordinate) {
                sx {
                    //                    marginTop = 10.px
                    marginBottom = 10.px
                }

                val selection =
                    if (props.primary) dataLoader.controller.secondaryCoordinateSelection else dataLoader.controller.primaryCoordinateSelection
                if (selection.isSelected(current)) {
                    Fab {
                        val color = theme.asDynamic().palette.text.primary
                        val backgroundColor = theme.asDynamic().palette.background.default
                        sx = unsafeJso {
                            position = Position.absolute
                            top = 1.px
                            right = 1.px
                            width = 24.px
                            height = 24.px
                            minWidth = 0.px
                            minHeight = 0.px
                            this.color = color
                            this.backgroundColor = backgroundColor
                        }

                        //            classes(classes.root)
                        size = Size.small
                        title = "Close"
                        ariaLabel = "Close"
                        //            color = ButtonColor.inherit
                        onClick = {
                            dataLoader.coordinateSelection.clearSelection()
                        }

                        CloseTwoTone {
                        }
                    }
                }

                CoordinateInfo {
                    this.probing = dataLoader.controller.primaryCoordinateProbing
                    this.selection = dataLoader.controller.primaryCoordinateSelection
                    this.dataLoader = dataLoader
                    this.coordinate = current
                }
            } else if ((props.hideCrossborder == null || getHideCrossBorder == null) && getPhysicalNetwork != null) {
                Fab {
                    val color = theme.asDynamic().palette.text.primary
                    val backgroundColor = theme.asDynamic().palette.background.default
                    sx = unsafeJso {
                        position = Position.absolute
                        top = 1.px
                        right = 1.px
                        width = 24.px
                        height = 24.px
                        minWidth = 0.px
                        minHeight = 0.px
                        this.color = color
                        this.backgroundColor = backgroundColor
                    }

                    //            classes(classes.root)
                    size = Size.small
                    title = "Zoom Out"
                    ariaLabel = "Zoom Out"
                    //            color = ButtonColor.inherit
                    onClick = {
                        dataLoader.zoomOut()
                    }

                    ZoomOutMapTwoTone {}
                }

                Box {
                    sx {
                        alignSelf = AlignSelf.center
                    }

                    Typography {
                        sx {
                            color = if (paletteType == PaletteType.dark) NamedColor.white else NamedColor.black
                        }

                        align = TypographyAlign.center
                        variant = TypographyVariant.subtitle2
                        component = span
                        +"Import"
                    }
                    Typography {
                        val lightgrey = rgb(167, 167, 167)
                        val darkgrey = rgb(87, 87, 87)

                        sx {
                            color = if (paletteType == PaletteType.dark) {
                                lightgrey
                            } else {
                                darkgrey
                            }
                        }

                        align = TypographyAlign.center
                        variant = TypographyVariant.subtitle2
                        component = span
                        +"/Export"
                    }
                }

                Box {
                    sx {
                        //                        marginTop = 10.px
                        //                    transform = "translatey(-16px) perspective(1254px) rotateX(50deg) scale(0.9);".unsafeCast<Transform>()
                        //                    transition = "transform 200ms, height 200ms;".unsafeCast<Transition>()
                        //                    height = 96.px
                        //                    hover {
                        //                        transform = Globals.unset
                        //                        transition = "transform 2s, height 2s;".unsafeCast<Transition>()
                        height = 144.px
                        width = 276.px
                        //                    }
                    }

                    CrossborderInfo {
                        crossborderNetwork = getPhysicalNetwork.crossborder
                    }

                    RiskInfo {
                        network = getSubstationNetwork
                        riskMask = context.dataLoader.riskMask
                    }
                }
            }

            if (extra != null) {
                if (extra is RiskComponent) {
                    Tabs {
                        this.value = riskTab
                        variant = TabsVariant.fullWidth
                        asDynamic().size = Size.small
                        onChange = { _, newValue -> setRiskTab(newValue.toString()) }

                        Tab {
                            sx {
                                textTransform = None.none
                            }
                            label = ReactNode("Lines")
                            this.value = "lines"
                        }
                    }

                        when (riskTab) {
                            "lines" -> {
                                Stack {
                                    direction = responsive(StackDirection.column)
                                    asDynamic().justifyContent = JustifyContent.center
                                    sx {
                                        marginTop = 10.px
                                        marginBottom = 10.px
                                    }

                                    for (line in extra.lines) {
                                        Stack {
                                            direction = responsive(StackDirection.row)
                                            asDynamic().justifyContent = JustifyContent.center

                                            LineButton {
                                                this.dataLoader = dataLoader
                                                this.primary = props.primary
                                                this.probing =
                                                    if (props.primary) dataLoader.controller.secondaryLogicalBranchProbing else dataLoader.controller.primaryLogicalBranchProbing
                                                this.selection =
                                                    if (props.primary) dataLoader.controller.secondaryLogicalBranchSelection else dataLoader.controller.primaryLogicalBranchSelection
                                                this.logicalBranchId = line.logicalBranchId
                                            }
                                        }
                                    }
                                }
                            }
                        }
                } else if (extra is Schema) {
                } else if (extraPath != null) {
                    val line: Line = getSubstationNetwork?.lines?.get(extra)!!

                    LinkInfoExtra {
                        this.primary = props.primary
                        this.dataLoader = dataLoader
                        this.link = extraPath
                        this.line = line
                        this.tooltipLinks = context.configuration.tooltipLinks
                        this.masterDateTime = masterDateTime.toRawString()
                        this.expertMode = expertMode
                        this.tab = context.linkTab
                    }
                } else if (extraPoint != null) {
                    val substation: Substation = getSubstationNetwork?.substations?.get(extra)!!

                    NodeInfoExtra {
                        this.primary = props.primary
                        this.dataLoader = dataLoader
                        this.node = extraPoint
                        this.substation = substation
                        this.tooltipNodes = context.configuration.tooltipNodes
                        this.tab = context.nodeTab
                    }
                } else if (extra is Coordinate) {
                    CoordinateInfoExtra {
                        this.network = getSubstationNetwork
                        this.primary = props.primary
                        this.coordinate = extra
                        this.coordinateTab = coordinateTab
                    }
                } else if (getPhysicalNetwork != null) {
                }
            }
        }
    }
}